import { isDesktopShell } from './isDesktopShell';

export type AppSource = 'web' | 'mobile' | 'desktop';

/**
 * Detects the application source based on the current environment
 * @returns 'desktop' for desktop shell, 'mobile' for iOS shell, 'web' for browser
 */
export const getAppSource = (): AppSource => {
  // Check for desktop shell first
  if (isDesktopShell()) {
    return 'desktop';
  }

  // Check for iOS shell (PWA or standalone mode)
  if (isIOSShell()) {
    return 'mobile';
  }

  // Default to web browser
  return 'web';
};

/**
 * Detects if the app is running in an iOS shell (PWA or standalone mode)
 * @returns true if running in iOS shell
 */
export const isIOSShell = (): boolean => {
  // Check for iOS standalone mode (PWA added to home screen)
  if (typeof window !== 'undefined' && window.navigator) {
    // iOS Safari in standalone mode
    if ((window.navigator as any).standalone === true) {
      return true;
    }

    // Check for iOS WebKit and standalone mode
    const userAgent = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    
    if (isIOS && isStandalone) {
      return true;
    }

    // Check for iOS WebView (in-app browser)
    if (isIOS && !window.navigator.userAgent.includes('Safari')) {
      return true;
    }
  }

  return false;
};

/**
 * Gets detailed shell information for debugging
 * @returns Object with shell detection details
 */
export const getShellInfo = () => {
  const userAgent = typeof window !== 'undefined' ? window.navigator.userAgent : '';
  const isStandalone = typeof window !== 'undefined' ? window.matchMedia('(display-mode: standalone)').matches : false;
  const isDesktop = isDesktopShell();
  const isIOS = isIOSShell();
  const appSource = getAppSource();

  return {
    userAgent,
    isStandalone,
    isDesktop,
    isIOS,
    appSource,
    hasWebView: typeof window !== 'undefined' && !!(window as any).chrome?.webview,
    hasStandalone: typeof window !== 'undefined' && (window.navigator as any).standalone === true
  };
};
